import type { DeviceSpecDevice, EdgeDevice, EdgeMachine, SpecMachine, SpecMachineItem } from '../../types'
import { newKey } from './model'

// 마스터데이터 [Edge → 명세 가져오기] — Edge 설비(라인 › 설비)를 명세 인터페이스로 (현장 우선).
//  · 설비코드 = 인터페이스 ID. 같은 ID 가 명세에 있으면 이름·설명을 Edge 값으로 (디바이스의 이름·설명처럼), 없으면 새 인터페이스
//  · 항목(알람·계측·파라미터·신호)은 전송코드로 맞춘다 — 같은 코드면 태그 연결만 Edge 값으로, 없으면 추가, 명세에만 있는 항목은 남긴다.
//    항목 설명은 가져오지 않는다 — LiveEdge 쪽 설명은 LiveEdge 자체의 설명일 수 있어 명세 설명과 섞이면 안 된다
//    (디바이스 태그의 설명을 가져오지 않는 것과 같다 — pullMerge). 새 항목도 설명은 비워 둔다
//  · 태그는 Edge 태그 → 그 Edge 디바이스 코드와 같은 코드인 명세 디바이스 + 주소 (명세에 그 디바이스가 없으면 새 항목은 태그 미연결, 있던 항목은 연결 유지)

export interface PulledItem { code: string; device: string; address: string }

/** Edge 태그 id → 명세 디바이스 키 + 주소 */
export function tagResolver(edgeDevices: EdgeDevice[], specDevices: DeviceSpecDevice[]) {
  const specKey = new Map(specDevices.map(d => [d.code.trim(), d.key]))
  const byId = new Map<string, { device: string; address: string }>()
  for (const e of edgeDevices) for (const t of e.tags) byId.set(t.id, { device: specKey.get(e.code) ?? '', address: t.address ?? '' })
  return (tagId: string) => byId.get(tagId) ?? { device: '', address: '' }
}

export function pulledItems(m: EdgeMachine, resolve: ReturnType<typeof tagResolver>): PulledItem[] {
  const seen = new Set<string>()
  const out: PulledItem[] = []
  for (const i of [...m.alarms, ...m.metes, ...m.params, ...m.signals]) {
    const code = i.code.trim()
    if (!code || seen.has(code)) continue
    seen.add(code)
    out.push({ code, ...resolve(i.tagId) })
  }
  return out
}

export type MachinePlan =
  | { kind: 'create'; items: number }
  | { kind: 'same' }
  | { kind: 'update'; add: number; change: number; specOnly: number; renamed: boolean }

// 있던 항목에는 태그 연결만 덮는다 — Edge 태그가 명세 디바이스로 안 이어지면(명세에 그 디바이스가 없음) 연결은 그대로
const overlay = (a: SpecMachineItem, b: PulledItem): SpecMachineItem =>
  b.device ? { ...a, device: b.device, address: b.address } : a
const sameItem = (a: SpecMachineItem, b: PulledItem) => {
  const c = overlay(a, b)
  return a.device === c.device && a.address.trim() === c.address.trim()
}

export function machinePlan(m: EdgeMachine, items: PulledItem[], spec: SpecMachine | undefined): MachinePlan {
  if (!spec) return { kind: 'create', items: items.length }
  const byCode = new Map(spec.items.map(i => [i.code.trim(), i]))
  let add = 0, change = 0
  for (const p of items) {
    const old = byCode.get(p.code)
    if (!old) add++
    else if (!sameItem(old, p)) change++
  }
  const codes = new Set(items.map(p => p.code))
  const specOnly = spec.items.filter(i => !codes.has(i.code.trim())).length
  const next = labels(m, spec)
  const renamed = (spec.name ?? '') !== next.name || spec.description !== next.description
  return add + change === 0 && !renamed ? { kind: 'same' } : { kind: 'update', add, change, specOnly, renamed }
}

// 인터페이스 이름·설명 — Edge 값, 비었으면 명세 값
export const labels = (m: EdgeMachine, spec: SpecMachine | undefined) => ({
  name: m.name.trim() || spec?.name || '', description: m.description?.trim() || spec?.description || '',
})

// ── 미리보기 비교 (CompareView 와 같은 규칙 — 기준은 명세, 가져오면 바뀌는 칸만 표시) ──

export type ItemField = 'description' | 'device' | 'address'
/** 가져오면 바뀔 수 있는 칸 — 태그 연결만 (항목 설명은 가져오지 않는다) */
export type LinkField = 'device' | 'address'
export type ItemAction = 'same' | 'change' | 'add' | 'keep'

/** 미리보기 한 줄 — 가져온 뒤 명세 순서(명세 항목 → 새 항목). spec = 지금 명세 값(새 항목이면 들어갈 값 — 설명은 빈 칸), edge = 같은 코드의 Edge 항목 */
export interface ItemRow {
  key: string
  code: string
  spec: Pick<SpecMachineItem, ItemField>
  edge: PulledItem | null
  action: ItemAction
  changed: LinkField[]
  /** Edge 태그의 디바이스가 명세에 없다 (새 항목은 태그 미연결, 있던 항목은 연결 유지) */
  edgeDeviceMissing: boolean
}

export function compareItems(items: PulledItem[], spec: SpecMachine | undefined): ItemRow[] {
  const byCode = new Map(items.map(p => [p.code, p]))
  const missing = (p: PulledItem | null) => !!p && !!p.address.trim() && !p.device
  const rows: ItemRow[] = (spec?.items ?? []).map(i => {
    const p = byCode.get(i.code.trim()) ?? null
    if (p) byCode.delete(p.code)
    if (!p) return { key: i.key, code: i.code, spec: i, edge: null, action: 'keep', changed: [], edgeDeviceMissing: false }
    const next = overlay(i, p)
    const changed = (['device', 'address'] as const).filter(f => (f === 'address' ? i.address.trim() !== next.address.trim() : i[f] !== next[f]))
    return { key: i.key, code: i.code, spec: i, edge: p, action: changed.length ? 'change' : 'same', changed, edgeDeviceMissing: missing(p) }
  })
  for (const p of byCode.values()) rows.push({ key: `new:${p.code}`, code: p.code, spec: { ...p, description: '' }, edge: p, action: 'add', changed: [], edgeDeviceMissing: missing(p) })
  return rows
}

/** 명세 인터페이스에 Edge 설비를 덮는다 (명세에만 있는 항목은 남김) */
export function mergeEdgeMachine(m: EdgeMachine, items: PulledItem[], spec: SpecMachine | undefined): SpecMachine {
  const base: SpecMachine = spec ?? { key: newKey(), code: m.code, name: '', description: '', items: [] }
  const byCode = new Map(items.map(p => [p.code, p]))
  const kept = base.items.map(i => {
    const p = byCode.get(i.code.trim())
    if (!p) return i
    byCode.delete(p.code)
    return sameItem(i, p) ? i : overlay(i, p)
  })
  const added = [...byCode.values()].map(p => ({ key: newKey(), ...p, description: '' }))
  return { ...base, ...labels(m, spec), items: [...kept, ...added] }
}
